import { useMutation, useQueryClient } from 'react-query';
import upsertCollectedPhotoCards from '../../../components/api/mutations/upsertCollectedPhotoCards';
import baseUrl from '@pocapan/shared/utils/baseUrl';
import getToken from '@pocapan/shared/utils/getToken';
import { QUERY_KEYS } from '../constants/queryKeys';
import { ImageSearchResponseType } from '../types/type';

export interface UpsertPhotoCardsToCollectRequestType {
  artistId: string;
  memberId: string;
  photoCards: {
    photo_card_id: string;
    collected_count: number;
  }[];
}

export const useUpsertPhotoCardsToCollectionMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    /**
     * 포토카드 컬렉션 업데이트
     * @param request artist_id, photo_cards 요청을 하기 위한 데이터, member_id : invalidateQueries 를 위한 데이터
     * @returns 포토카드 컬렉션 업데이트 응답
     */
    mutationFn: async (request: UpsertPhotoCardsToCollectRequestType) => {
      const response = await upsertPhotoCardsToCollection(request.artistId, request.photoCards);
      return response;
    },
    onSuccess: (_, variables) => {
      //포토카드 관련 데이터 무효화
      variables.photoCards.forEach((photoCard) => {
        queryClient.invalidateQueries(['photoCardV1', photoCard.photo_card_id]);
      });
      queryClient.invalidateQueries(['photoCardEvents', variables.artistId, variables.memberId]);
    }
  });
};

/**
 * 포토카드들을 컬렉션에 추가/업데이트합니다
 * @param artistId 아티스트 ID
 * @param photoCards 포토카드 배열 [{photo_card_id: string, collected_count: number}]
 * @returns API 응답
 */
export default async function upsertPhotoCardsToCollection(
  artistId: string,
  photoCards: UpsertPhotoCardsToCollectRequestType['photoCards']
) {
  if (!photoCards || photoCards.length === 0) {
    throw new Error('photoCards 배열이 비어있습니다');
  }

  const response = await fetch(
    `${baseUrl()}/api/v1/artists/${artistId}/photo_cards/upsert_collected`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${getToken()}`
      },
      body: JSON.stringify({
        photo_cards: photoCards
      })
    }
  );

  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`);
  }

  return response.json();
}
